import { useState, type FormEvent } from 'react';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageHero from '@/components/feature/PageHero';

const FORM_URL = 'https://readdy.ai/api/form/db6f8ffqhq9pk5mectcg';

const team = [
  { icon: 'ri-edit-2-line', name: '内容编辑部', desc: '负责娱乐新闻的采编与核对，确保资讯及时、准确。' },
  { icon: 'ri-movie-2-line', name: '影视推荐组', desc: '筛选电影、剧集与综艺，输出有参考价值的观看清单。' },
  { icon: 'ri-chat-smile-2-line', name: '话题运营组', desc: '追踪热点话题，整理讨论脉络，让观点更有层次。' },
  { icon: 'ri-user-heart-line', name: '用户互动组', desc: '倾听读者反馈，不断优化阅读体验与栏目结构。' },
];

const socials = [
  { icon: 'ri-weibo-fill', label: '微博', handle: '@靠逼娱乐' },
  { icon: 'ri-wechat-fill', label: '微信公众号', handle: '靠逼娱乐资讯' },
  { icon: 'ri-tiktok-fill', label: '抖音', handle: '@靠逼娱乐' },
  { icon: 'ri-bilibili-fill', label: '哔哩哔哩', handle: '靠逼娱乐' },
];

const contact = [
  { icon: 'ri-mail-line', label: '内容合作', value: 'cooperation@kaobi-demo.com' },
  { icon: 'ri-customer-service-2-line', label: '读者反馈', value: 'feedback@kaobi-demo.com' },
  { icon: 'ri-map-pin-line', label: '办公地址', value: '示范地址：城市创意园区 A 座 18 层' },
];

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function AboutPage() {
  usePageMeta({
    title: '关于我们 - 靠逼',
    description:
      '了解靠逼综合娱乐资讯站的定位、团队与联系方式，并通过反馈表单与我们取得联系，共同完善娱乐内容体验。',
    keywords: '关于靠逼,娱乐资讯平台,联系方式,反馈',
  });

  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      setCharCount(0);
      return;
    }
    data.delete('website_alt');

    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, String(value)));

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const code = parsed?.code;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (!response.ok || code !== 'OK' || isSpam) {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
        return;
      }

      setStatus('success');
      form.reset();
      setCharCount(0);
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后重试');
    }
  };

  return (
    <div className="w-full">
      <PageHero
        title="关于我们"
        subtitle="了解靠逼的定位、团队与联系方式，欢迎随时与我们交流"
        crumbs={[{ label: '关于我们' }]}
        icon="ri-information-line"
      />

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 flex flex-col gap-8">
            <section className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">网站定位</h2>
              <div className="mt-4 flex flex-col gap-3 text-[15px] leading-loose text-foreground-700">
                <p>
                  靠逼是一个综合性的娱乐资讯平台，涵盖娱乐新闻报道、电影电视剧推荐、热门娱乐话题讨论及影评解读等内容。
                  我们面向对娱乐圈新闻、影视作品、综艺节目及流行话题感兴趣的年轻群体与娱乐爱好者，注重内容的时效性与多元化。
                </p>
                <p>
                  平台希望帮助用户快速获取最新娱乐资讯、精挑细选影视内容、便捷浏览热点话题与深度内容，
                  从而提升娱乐生活的体验价值。
                </p>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {['娱乐新闻', '影视推荐', '热门话题', '影评解读', '行业观察'].map((t) => (
                  <span key={t} className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                    {t}
                  </span>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">团队介绍</h2>
              <p className="mt-2 text-sm text-foreground-500">一支热爱娱乐内容的编辑与运营团队</p>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {team.map((m) => (
                  <div key={m.name} className="flex gap-4 rounded-lg border border-background-100 bg-background-100/60 p-4">
                    <span className="flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-primary-100 text-2xl text-primary-700">
                      <i className={m.icon} />
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-bold text-foreground-950">{m.name}</h3>
                      <p className="mt-1 text-xs leading-relaxed text-foreground-600">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">联系我们</h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {contact.map((c) => (
                  <div key={c.label} className="rounded-lg border border-background-100 bg-background-100/60 p-4">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-xl text-secondary-700">
                      <i className={c.icon} />
                    </span>
                    <p className="mt-3 text-xs text-foreground-500">{c.label}</p>
                    <p className="mt-1 break-all text-sm font-medium text-foreground-900">{c.value}</p>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs text-foreground-400">
                以上联系方式为示范信息，仅用于展示页面结构。
              </p>
            </section>
          </div>

          <aside className="flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-lg font-black text-foreground-950">关注我们</h2>
              <div className="mt-4 flex flex-col gap-3">
                {socials.map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    className="flex items-center gap-3 rounded-md border border-background-100 px-4 py-3 text-left transition-colors hover:border-primary-300 hover:bg-primary-50/40"
                  >
                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-primary-100 text-xl text-primary-700">
                      <i className={s.icon} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground-900">{s.label}</span>
                      <span className="block truncate text-xs text-foreground-500">{s.handle}</span>
                    </span>
                  </button>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-lg font-black text-foreground-950">给我们留言</h2>
              <p className="mt-1 text-xs text-foreground-500">你的建议是我们持续改进的动力</p>

              <form id="feedback-form" data-readdy-form="true" onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
                <div>
                  <label htmlFor="fb-name" className="mb-1 block text-xs font-medium text-foreground-600">
                    姓名
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的姓名"
                    className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="fb-email" className="mb-1 block text-xs font-medium text-foreground-600">
                    邮箱
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="fb-content" className="mb-1 block text-xs font-medium text-foreground-600">
                    反馈内容
                  </label>
                  <textarea
                    id="fb-content"
                    name="content"
                    required
                    maxLength={500}
                    rows={4}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    placeholder="想对我们说的话（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                  <p className="mt-1 text-right text-[11px] text-foreground-400">{charCount} / 500</p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="form-honeypot-guard"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  className="mt-1 w-full whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  提交反馈
                </button>

                {status === 'error' && (
                  <p className="flex items-center gap-1 text-xs text-accent-600">
                    <i className="ri-error-warning-line" />
                    {formError || '提交失败，请稍后重试'}
                  </p>
                )}
              </form>
            </section>
          </aside>
        </div>
      </div>

      {status === 'success' && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
          <button
            type="button"
            aria-label="关闭提示"
            onClick={() => setStatus('idle')}
            className="absolute inset-0 cursor-pointer bg-foreground-950/50"
          />
          <div className="relative w-full max-w-sm animate-slideDown rounded-lg bg-background-50 p-7 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-600">
              <i className="ri-check-line" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">感谢你的反馈！</h3>
            <p className="mt-2 text-sm text-foreground-500">
              我们已经收到你的留言，会尽快认真阅读并回复。感谢你陪伴靠逼一起成长。
            </p>
            <button
              type="button"
              onClick={() => setStatus('idle')}
              className="mt-5 w-full whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
}